import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 20; // ~40s
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed | unknown
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("unknown");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${API}/payments/status/${sessionId}`
        );
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("unknown");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        // Unknown/missing session — bail early rather than polling for 40s
        if (e?.response?.status === 404) {
          setStatus("unknown");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("unknown");
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container max-w-3xl">
        <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
          <span className="w-8 h-px bg-[color:var(--sr-accent)]" />
          {status === "paid" ? "Order confirmed" : "Confirming payment"}
        </p>

        {status === "checking" && (
          <div data-testid="payment-checking">
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-[color:var(--sr-primary)]">
              Just a moment.
            </h1>
            <p className="mt-6 text-[color:var(--sr-text)]/75 leading-relaxed max-w-md">
              We&apos;re confirming your payment with Stripe. This usually takes a
              few seconds.
            </p>
            <div className="mt-10 flex items-center gap-3 text-[color:var(--sr-muted)] text-xs tracking-[0.28em] uppercase">
              <span className="w-2 h-2 rounded-full bg-[color:var(--sr-accent)] animate-pulse" />
              Working
            </div>
          </div>
        )}

        {status === "paid" && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="payment-paid"
          >
            <div className="mb-8 w-14 h-14 rounded-full border hairline flex items-center justify-center text-[color:var(--sr-primary)]">
              <Check size={22} strokeWidth={1.25} />
            </div>
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-[color:var(--sr-primary)]">
              Thank you.
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                Yours is on the way.
              </em>
            </h1>
            <div className="mt-10 border-t hairline pt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
              <div>
                <p className="label-eyebrow text-[color:var(--sr-muted)] mb-2">
                  Order
                </p>
                <p
                  data-testid="order-id"
                  className="font-serif text-xl text-[color:var(--sr-primary)] break-all"
                >
                  {sessionId?.slice(-14)}
                </p>
              </div>
              {order?.amount_total != null && (
                <div>
                  <p className="label-eyebrow text-[color:var(--sr-muted)] mb-2">
                    Total charged
                  </p>
                  <p className="font-serif text-xl text-[color:var(--sr-primary)] tabular-nums">
                    ${(order.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              )}
              {order?.email && (
                <div className="md:col-span-2">
                  <p className="label-eyebrow text-[color:var(--sr-muted)] mb-2">
                    Receipt sent to
                  </p>
                  <p className="text-[color:var(--sr-primary)]">
                    {order.email}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-14 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="continue-shopping"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
              >
                Continue shopping
                <ArrowRight size={16} strokeWidth={1.25} />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-3 px-8 py-4 label-eyebrow text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
              >
                Return home
              </Link>
            </div>
          </motion.div>
        )}

        {["expired", "failed", "unknown"].includes(status) && (
          <div data-testid="payment-issue">
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-[color:var(--sr-primary)]">
              Something got in the way.
            </h1>
            <p className="mt-6 text-[color:var(--sr-text)]/75 leading-relaxed max-w-md">
              We couldn&apos;t confirm your payment. If you were charged, please
              contact us at{" "}
              <a
                href="mailto:smilingroots@gmail.com"
                className="underline decoration-[color:var(--sr-accent)]"
              >
                smilingroots@gmail.com
              </a>
              . Otherwise, your bag is still saved — try again.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="inline-flex items-center gap-3 px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
              >
                Return to shop
                <ArrowRight size={16} strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
